import React, { useEffect, useMemo, useRef, useState } from "react";
import { decode, fmt, bounded } from "../lib/result_decode";

// Result Decoder on /start/reported-statistic. Paste a result as a paper prints it; each result found is read
// back piece by piece, matched to the tests that could have produced it, checked for consistency (as statcheck
// does) and converted to effect sizes. Everything runs here in the browser.
// Props: tests ([{ slug, name, path, statistics: [term slugs] }], published only) and terms ({ slug: { term,
// symbol, path, note, benchmarks: [{ range, label, threshold, meaning }] } }), both from StartController#statistic.

const EXAMPLES = [
	"t(39) = −2.64, p = .012, d = −0.42",
	"Sleep differed across the three majors, F(2, 87) = 4.12, p = .020, ηp² = .09. Sleep was related to GPA, r(38) = .45, p = .004, but the two programs did not differ in hours studied, t(58) = 2.31, ns.",
	"Majors chose different study spots, χ2(2, N = 120) = 7.10, p = .029, Cramér's V = .24.",
	"Ratings were higher after training, U = 45, z = −2.10, p = .036, r = .33, N = 40.",
	"Training raised the odds of passing, OR = 2.31, 95% CI [1.40, 3.81], p = .04."
];

// *x* is a math-italic symbol; *x_y* adds a subscript.
function Rich({ text }) {
	if (!text) return null;
	// Keep "p = .30" together on one line.
	const parts = String(text).replace(/\* ([=<>≤≥]) (?=[−-]?[.\d])/g, "* $1 ").split(/(\*[^*]+\*)/g);
	return parts.map((p, i) => (p.startsWith("*") && p.endsWith("*") && p.length > 2 ? <Sym key={i} text={p.slice(1, -1)} /> : <React.Fragment key={i}>{p}</React.Fragment>));
}

// The paste as printed, with its statistical symbols in math italics.
const RAW_SYMBOL = /(?<![A-Za-zα-ω'])(χ²|η²p|η²G|η²|ω²|ε²|R²|rₛ|SD|SE|Mdn|dz|[tFpdrzUWVTHQNnMbgwV])(?=\s?[([=<>≤≥])/g;
function RawResult({ text }) {
	const parts = String(text).split(RAW_SYMBOL);
	return parts.map((p, i) => (i % 2 ? (p === "OR" || p === "SD" || p === "SE" || p === "Mdn" ? <em key={i}>{p}</em> : <em key={i}>{p.replace(/^(η²|ω²)(p|G)$/, "$1")}{/^(η²|ω²)(p|G)$/.test(p) && <sub>{p.slice(-1)}</sub>}</em>) : <React.Fragment key={i}>{p}</React.Fragment>));
}

// Names like "Cohen's d" with their one-letter symbol in italics (names already marked up are left alone).
const nameMarkup = (n) => (String(n).includes("*") ? n : String(n).replace(/(?<![A-Za-z'|])([dgrwVWf])(?![A-Za-z'|])/g, "*$1*"));

function Sym({ text }) {
	const [main, sub] = String(text).split("_");
	return <em className="font-serif">{main}{sub && <sub>{sub}</sub>}</em>;
}

// Test names put t, z and F in italics, as the site's test_name helper does.
function TestName({ name }) {
	const parts = String(name).split(/(?<![A-Za-z])([tzF])(?=-)/);
	return parts.map((p, i) => (i % 2 ? <em key={i}>{p}</em> : <React.Fragment key={i}>{p}</React.Fragment>));
}

function TermLink({ term, children }) {
	if (!term || !term.path) return children;
	return <a href={term.path} className="link">{children}</a>;
}

const STATUS = {
	consistent: { icon: "✓", box: "border-success bg-white", head: "text-success" },
	"one-tailed": { icon: "✓", box: "border-success bg-white", head: "text-success" },
	// A small mismatch is a caution, not an alarm; red is kept for decision errors.
	inconsistent: { icon: "!", box: "border-warning bg-white", head: "text-warning" },
	decision: { icon: "✕", box: "border-danger bg-danger text-white", head: "text-white" },
	impossible: { icon: "✕", box: "border-danger bg-danger text-white", head: "text-white" },
	unchecked: { icon: "–", box: "border-gray-300 bg-gray-50", head: "text-gray-700" }
};

function Check({ check }) {
	const s = STATUS[check.status] || STATUS.unchecked;
	const strong = check.status === "decision" || check.status === "impossible";
	return (
		<div className={`rounded-[3px] border-2 px-4 py-3 ${s.box}`} role={strong ? "alert" : undefined}>
			<p className={`flex items-center gap-2 font-semibold ${s.head}`}>
				<span aria-hidden="true" className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-sm font-bold ${strong ? "bg-white text-danger" : check.status === "unchecked" ? "bg-gray-300 text-gray-800" : check.status === "inconsistent" ? "bg-warning text-white" : "bg-success text-white"}`}>{s.icon}</span>
				<span><span className="sr-only">Consistency check: </span>{check.headline}</span>
			</p>
			<p className={`mt-1 text-sm leading-relaxed ${strong ? "text-white" : "text-gray-800"}`}><Rich text={check.message} /></p>
			{check.after && <p className={`mt-1 text-sm ${strong ? "text-white/90" : "text-gray-700"}`}>{check.after}</p>}
			{check.notes && check.notes.map((n, i) => <p key={i} className={`mt-1 text-xs ${strong ? "text-white/90" : "text-gray-600"}`}><Rich text={n} /></p>)}
		</div>
	);
}

const Eyebrow = ({ children }) => <p className="eyebrow text-[0.7rem] text-teal">{children}</p>;

function Band({ band, term }) {
	if (!band) return null;
	return (
		<span className="mt-1 inline-flex flex-wrap items-baseline gap-x-2 text-sm">
			<span className="rounded bg-gray-100 px-1.5 py-0.5 font-semibold text-ink">{band.label}</span>
			<span className="font-serif text-gray-700">{band.range}</span>
			{term?.path && <a href={term.path} className="link text-xs">Benchmarks →</a>}
		</span>
	);
}

function effectValue(key, v, dec = 2) {
	return fmt(v, dec, !bounded(key));
}
function ciText(ci, key, dec = 2) {
	if (!ci) return null;
	return `[${effectValue(key, ci[0], dec)}, ${effectValue(key, ci[1], dec)}]`;
}

function Effects({ r, terms }) {
	if (!r.reported.length && !r.computed.length) return null;
	const reportedKeys = new Set(r.reported.map((e) => e.key));
	const notes = new Set();
	return (
		<section className="mt-6">
			<Eyebrow>How Big Is It?</Eyebrow>
			<ul className="mt-2 space-y-2">
				{r.reported.map((e, i) => {
					const term = terms[e.term];
					if (term?.note) notes.add(term.note);
					const c = e.comparison;
					return (
						<li key={`r${i}`} className="rounded-lg bg-gray-50 px-3 py-2">
							<p><span className="text-xs text-gray-600">Reported: </span><TermLink term={term}><Sym text={e.symbol.replace("²p", "²_p").replace("²G", "²_G").replace(/^dz$/, "d_z")} /></TermLink> <span className="font-serif">{e.comp} {effectValue(e.key, e.value, Math.max(2, e.decimals))}</span>
								{e.ci && <span className="font-serif text-gray-700">, {e.ci.level}% CI [{effectValue(e.key, e.ci.lo, Math.max(2, e.ci.loDecimals))}, {effectValue(e.key, e.ci.hi, Math.max(2, e.ci.hiDecimals))}]</span>}
								{e.name !== e.symbol && <span className="text-gray-600"> (<Rich text={nameMarkup(e.name)} />)</span>}</p>
							<Band band={e.band} term={term} />
							{c && <p className={`mt-1 flex gap-1.5 text-sm ${c.status === "match" ? "text-gray-800" : "text-gray-800"}`}>
								<span aria-hidden="true" className={`font-bold ${c.status === "match" ? "text-success" : "text-danger"}`}>{c.status === "match" ? "✓" : "!"}</span>
								<span><span className="font-semibold">{c.status === "match" ? "Checks out." : c.status === "sign" ? "Opposite sign." : "Doesn't match."}</span> <Rich text={c.message} /></span></p>}
						</li>
					);
				})}
				{r.computed.filter((c) => !c.reported || !reportedKeys.has(c.key)).map((e, i) => {
					const term = terms[e.term];
					if (term?.note && e.band) notes.add(term.note);
					return (
						<li key={`c${i}`} className="rounded-lg bg-gray-50 px-3 py-2">
							<p><span className="text-xs text-gray-600">{e.reported ? "Reported: " : "From the statistic: "}</span><TermLink term={term}><Sym text={e.symbol} /></TermLink> <span className="font-serif">= {effectValue(e.key, e.value)}</span>
								{e.ci && <span className="font-serif text-gray-700">, 95% CI <span className="whitespace-nowrap">{ciText(e.ci, e.key)}</span></span>}
								{e.name !== e.symbol && <span className="text-gray-600"> (<Rich text={nameMarkup(e.name)} />)</span>}</p>
							{e.assumption ? <p className="mt-0.5 text-sm text-gray-700"><span className="font-semibold">If</span> <Rich text={e.assumption} />.</p> : !e.reported && <p className="mt-0.5 text-sm text-gray-700">Needs no assumption about the design.</p>}
							<Band band={e.band} term={term} />
							<p className="mt-0.5 text-xs text-gray-600"><Rich text={e.how} />{!e.ci && ["r", "eta2p"].includes(e.key) && !e.reported && " No interval: the statistic is too large for the noncentral distributions here."}</p>
						</li>
					);
				})}
			</ul>
			{r.computed.some((c) => c.assumption) && <p className="mt-2 text-xs text-gray-600">Values that depend on the design are alternatives: only one design produced this result. Intervals are 95%, from the noncentral <em>t</em> or <em>F</em> distribution as R's effectsize computes them.</p>}
			{[...notes].map((n, i) => <p key={i} className="mt-1 text-xs text-gray-600">{n}</p>)}
		</section>
	);
}

// Model-fit indices: what each says, the site's benchmarks where the glossary has them, and what can be checked.
function Fits({ fits, terms }) {
	if (!fits || !fits.length) return null;
	const notes = new Set();
	return (
		<section className="mt-6">
			<Eyebrow>Model Fit</Eyebrow>
			<ul className="mt-2 space-y-2">
				{fits.map((f, i) => {
					const term = f.term ? terms[f.term] : null;
					if (term?.note) notes.add(term.note);
					const c = f.check;
					return (
						<li key={i} className="rounded-lg bg-gray-50 px-3 py-2">
							<p><TermLink term={term}><span className="font-semibold">{f.label === "χ²/df" ? <><Sym text="χ²" />/<Sym text="df" /></> : f.label}</span></TermLink>{" "}
								<span className="font-serif">{f.comp} {fmt(f.value, f.decimals, !["cfi", "tli", "rmsea", "srmr", "gfi", "agfi"].includes(f.key))}</span>
								{f.ciText && <span className="font-serif text-gray-700">, <span className="whitespace-nowrap">{f.ciText}</span></span>}
								<span className="text-gray-600"> ({f.name})</span></p>
							<p className="mt-0.5 text-sm text-gray-700">{f.text}</p>
							<Band band={f.band} term={term} />
							{c && c.message && <p className="mt-1 flex gap-1.5 text-sm text-gray-800">
								<span aria-hidden="true" className={`font-bold ${c.status === "match" ? "text-success" : c.status === "differs" ? "text-danger" : "text-gray-500"}`}>{c.status === "match" ? "✓" : c.status === "differs" ? "!" : "–"}</span>
								<span>{c.status === "match" && <span className="font-semibold">Checks out. </span>}{c.status === "differs" && <span className="font-semibold">Doesn't match. </span>}<Rich text={c.message} /></span></p>}
							{f.ciNote && <p className="mt-1 text-sm text-gray-700">{f.ciNote}</p>}
						</li>
					);
				})}
			</ul>
			{[...notes].map((n, i) => <p key={i} className="mt-1 text-xs text-gray-600">{n}</p>)}
		</section>
	);
}

function Evidence({ ev, term }) {
	if (!ev || !term) return null;
	const basis = { recomputed: "the recomputed p", "one-tailed": "the one-tailed p", reported: "the reported p", bound: "the reported bound" }[ev.basis];
	return (
		<section className="mt-6">
			<Eyebrow>{ev.fitMeaning ? "Strength of Evidence Against the Model" : "Strength of Evidence"}</Eyebrow>
			{ev.band ? (
				<>
					<p className="mt-1 text-sm text-gray-800"><span className="rounded bg-gray-100 px-1.5 py-0.5 font-semibold text-ink">{ev.band.label}</span> <span className="ml-1 font-serif text-gray-700">{ev.band.range}</span> <span className="text-gray-600">(from {basis})</span></p>
					<p className="mt-1 text-sm text-gray-700">{ev.fitMeaning || ev.band.meaning}</p>
				</>
			) : ev.range && ev.range.length ? (
				<p className="mt-1 text-sm text-gray-700">The result gives only a bound, so it could sit anywhere from <span className="font-semibold">{ev.range[ev.range.length - 1].label}</span> to <span className="font-semibold">{ev.range[0].label}</span>. The exact p would say which.</p>
			) : null}
			{term.note && <p className="mt-1 text-xs text-gray-600">{term.note} <a href={term.path} className="link">The ladder →</a></p>}
		</section>
	);
}

function Tests({ tests }) {
	const { ranked, others } = tests;
	if (!ranked.length && !others.length) return null;
	return (
		// The take-home, so it leads the card in its own box.
		<section className="rounded-xl border border-teal/40 bg-teal-50 p-4">
			<Eyebrow>Which Test Could It Be?</Eyebrow>
			<p className="mt-1 text-sm text-gray-600">Possibilities from the df and the words around the result, most likely first. The numbers alone can't prove which.</p>
			<ol className="mt-2 divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white">
				{ranked.map((t, i) => (
					<li key={i} className="flex flex-col gap-1 px-4 py-2.5 sm:flex-row sm:items-baseline sm:gap-3">
						<span className="w-28 flex-none text-xs font-semibold uppercase tracking-wide text-spruce-700">{t.likelihood}</span>
						<span className="min-w-0 text-sm">
							{t.path ? <a href={t.path} className="link font-semibold"><TestName name={t.name} /></a> : <span className="font-semibold text-ink"><TestName name={t.name} /></span>}
							{t.why && <span className="text-gray-700"> · <Rich text={t.why} /></span>}
						</span>
					</li>
				))}
			</ol>
			{others.length > 0 && <p className="mt-2 text-sm text-gray-700">Other methods on this site that report it: {others.map((t, i) => <React.Fragment key={t.slug}>{i > 0 && " · "}<a href={t.path} className="link"><TestName name={t.name} /></a></React.Fragment>)}.</p>}
		</section>
	);
}

function ResultCard({ r, terms, index, total }) {
	if (r.failed) {
		return <div className="rounded-xl border border-gray-200 px-5 py-4 text-sm text-gray-700">I couldn't read this part: <span className="font-serif">{r.raw}</span></div>;
	}
	const statTerm = terms[r.info.term];
	return (
		<article className="overflow-hidden rounded-[3px] border border-teal bg-white">
			<header className="bg-teal px-5 py-3 text-white">
				{total > 1 && <span className="note-card-tag float-right mt-1 ml-3 text-white/70">Result {index + 1} of {total}</span>}
				<p className="font-serif text-lg leading-snug break-words"><RawResult text={r.raw} /></p>
			</header>
			<div className="px-5 py-4">
				<Tests tests={r.tests} />
				<div className={r.tests.ranked.length || r.tests.others.length ? "mt-6" : ""}><Check check={r.check} /></div>
				<section className="mt-6">
					<Eyebrow>Reading Each Piece</Eyebrow>
					<p className="mt-1 text-sm text-gray-700">
						A{/^([aeiouAEIOU]|[FHLMNRSX]\b)/.test(r.info.name.replace(/\*/g, "")) ? "n" : ""} <TermLink term={statTerm}><Rich text={r.info.name} /></TermLink>{r.info.showSymbol && r.info.symbol && <> (<Sym text={r.info.symbol} />)</>}.
					</p>
					{(r.pieces.length > 0 || r.direction || r.others.length > 0) && <dl className="mt-2 divide-y divide-gray-200 rounded-xl border border-gray-200 text-sm">
						{r.pieces.map((p, i) => (
							<div key={i} className="grid gap-1 px-4 py-2.5 sm:grid-cols-[9rem_1fr] sm:gap-4">
								<dt className="font-serif font-semibold text-ink">
									<TermLink term={terms[p.term]}>{/^[^\s()0-9%]+$/.test(p.label) ? <Sym text={p.label} /> : p.label}</TermLink>
									{p.value && <span className="font-normal"> {p.value}</span>}
								</dt>
								<dd className="text-gray-700">
									<Rich text={p.text} />
									{p.list && <ul className="mt-1 list-disc space-y-0.5 pl-5">{p.list.map((l, j) => <li key={j}><Rich text={l} /></li>)}</ul>}
								</dd>
							</div>
						))}
						{r.direction && (
							<div className="grid gap-1 px-4 py-2.5 sm:grid-cols-[9rem_1fr] sm:gap-4">
								<dt className="font-semibold text-ink">Direction</dt>
								<dd className="text-gray-700"><Rich text={r.direction} /></dd>
							</div>
						)}
						{r.others.length > 0 && (
							<div className="grid gap-1 px-4 py-2.5 sm:grid-cols-[9rem_1fr] sm:gap-4">
								<dt className="font-semibold text-ink">Also Here</dt>
								<dd className="text-gray-700">{r.others.map((o, i) => <React.Fragment key={i}>{i > 0 && "; "}<span className="font-serif">{o.raw}</span> ({o.meaning})</React.Fragment>)}</dd>
							</div>
						)}
					</dl>}
				</section>
				<Fits fits={r.fits} terms={terms} />
				<Effects r={r} terms={terms} />
				<Evidence ev={r.evidence} term={terms["p-value"]} />
			</div>
		</article>
	);
}

export default function ResultDecoder({ tests = [], terms = {} }) {
	// A link can open the decoder on an example (?q=…); what a reader pastes never goes in the URL.
	const linked = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("q") || "" : "";
	const [text, setText] = useState(linked);
	const [shown, setShown] = useState(linked);
	const [example, setExample] = useState(0);
	const timer = useRef(null);
	useEffect(() => {
		clearTimeout(timer.current);
		timer.current = setTimeout(() => setShown(text), 300);
		return () => clearTimeout(timer.current);
	}, [text]);
	const out = useMemo(() => {
		try { return decode(shown, { tests, terms }); } catch { return { results: [], unread: [{ text: shown.slice(0, 200) }], orphans: [] }; }
	}, [shown, tests, terms]);
	const tryExample = () => {
		const next = EXAMPLES[example % EXAMPLES.length];
		setText(next);
		setShown(next);
		setExample(example + 1);
	};
	const nothing = shown.trim() && !out.results.length;
	return (
		<div>
			<label htmlFor="result-decoder-input" className="text-base font-semibold text-ink">Paste a Result</label>
			<p className="mt-0.5 text-sm text-gray-600">One result or a whole results paragraph, copied from the paper as it is.</p>
			<textarea id="result-decoder-input" value={text} onChange={(e) => setText(e.target.value)} rows={4} spellCheck={false}
				placeholder="Paste here, e.g. t(39) = −2.64, p = .012"
				className="mt-2 block w-full rounded-[3px] border border-gray-300 bg-white px-4 py-3 font-serif text-lg leading-snug text-ink placeholder:text-gray-400 focus:border-teal focus:ring-2 focus:ring-teal/30 focus:outline-none" />
			<div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
				<p className="text-sm text-gray-600">Runs in your browser: nothing you paste leaves this page.</p>
				<div className="flex gap-3">
					{text && <button type="button" onClick={() => { setText(""); setShown(""); }} className="link cursor-pointer text-sm">Clear</button>}
					<button type="button" onClick={tryExample} className="cursor-pointer rounded-[3px] bg-teal px-4 py-2 text-sm font-semibold text-white hover:bg-teal-600">{example ? "Try Another Example" : "Try an Example"}</button>
				</div>
			</div>

			<div className="mt-6 space-y-6" aria-live="polite">
				{!shown.trim() && <p className="rounded-xl bg-gray-50 px-5 py-4 text-sm text-gray-700">Each result you paste gets a card: what each number means, the tests that could have produced it, whether the p-value fits the statistic, and how big the effect is.</p>}
				{nothing && <p className="rounded-xl border border-gray-200 px-5 py-4 text-sm text-gray-700">I couldn't find a result I recognize. Results look like <span className="font-serif"><em>t</em>(39) = −2.64, <em>p</em> = .012</span>: a statistic, its degrees of freedom and a p-value.</p>}
				{out.results.map((r, i) => <ResultCard key={`${i}-${r.raw}`} r={r} terms={terms} index={i} total={out.results.length} />)}
				{(out.unread.length > 0 || out.orphans.length > 0) && (
					<div className="rounded-xl border border-gray-200 px-5 py-4 text-sm text-gray-700">
						{out.unread.map((u, i) => <p key={`u${i}`}><span className="font-semibold text-ink">I couldn't read this part:</span> <span className="font-serif">{u.text}</span></p>)}
						{out.orphans.map((o, i) => <p key={`o${i}`}><span className="font-semibold text-ink">Found without a statistic:</span> <span className="font-serif">{o.raw}</span>. {o.kind === "p" ? "A p-value can only be checked against the test statistic and df it came from." : "It needs the test statistic it belongs to."}</p>)}
					</div>
				)}
			</div>
			{out.results.length > 0 && (
				<p className="mt-4 text-xs text-gray-500">
					The consistency check follows statcheck (Nuijten et al., 2016): p is recomputed two-tailed from the statistic and df, allowing for the statistic's rounding, and a mismatch that changes significance at .05 is a decision error. Like statcheck, it can't say which number holds the mistake.
				</p>
			)}
		</div>
	);
}
